import { Card } from '../Card';
import { useGetComponent } from 'hooks/useGetComponent';
import { componentIds, pageIds } from 'utilities/constants';
import { useModal } from 'layout/useModal';
import { VirtualPharmaciesModal } from '../../../my-patients/components/VirtualPharmaciesModal';
import Spinner from 'components/Spinner';
import {
  GetFhirMedicationRequestListDocument,
  GetFhirMedicationRequestListQuery,
  useGetFhirMedicationRequestListLazyQuery,
} from 'graphql/generated/remote-schema-hasura';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import ButtonComponent from 'components/button/buttonComponent';
import { ReactComponent as PlusIcon } from 'assets/icons/addIcon.svg';
import MedicationCard from '../../../my-patients/pages/patient/tabs/supplements/card';
import MedicationCardVeradigm from '../../../my-patients/pages/patient/tabs/medications/card';
import { useGetPage } from 'hooks/useGetPage';
import {
  EPrescribeSsoMode,
  MedicationRequestSource,
  useAppointmentThirdPartySubscription,
  useGetThirdPartyConfigQuery,
  usePrescribeVeradigmMutation,
  useSyncEPrescribeMedicationsByPatientMutation,
} from 'graphql/generated/hasura';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { VERADIGM } from '../recommended-products/complete/useRecommendedProducts';
import { useCustomToast } from '../../../../layout/useToast';
import VeradigmModal from '../../../my-patients/pages/patient/VeradigmModal';
import moment from 'moment';
import { STANDARD_DATE } from 'utilities/dateHelper';

interface MedicationsProps {
  patientCodexId: string;
  appointmentCodexId: string;
  title?: string;
  encounterId: string;
  isEditMode?: boolean;
}

export const Medications = ({
  patientCodexId,
  appointmentCodexId,
  encounterId,
  title,
  isEditMode = true,
}: MedicationsProps) => {
  const { data: locale, loading } = useGetComponent({
    locale: 'en',
    componentId: componentIds.MEDICATION,
  });

  const { data: medicationLocale, loading: medicationLocaleLoading } =
    useGetPage({
      locale: 'en',
      pageId: pageIds.MEDICATION_HISTORY,
    });

  useGetThirdPartyConfigQuery({
    context: {
      ignoreGlobalErrors: true,
    },
  });

  const [artificialLoad, setArtificialLoad] = useState(false);

  const [medicationsData, setMedicationsData] =
    useState<GetFhirMedicationRequestListQuery | null>();

  const [getFHIRMedicationsByPatientCodexId, { loading: networkLoading }] =
    useGetFhirMedicationRequestListLazyQuery({
      fetchPolicy: 'network-only',
      onCompleted: (data) => {
        setArtificialLoad(false);
        setMedicationsData(data);
      },
      onError: () => {
        setArtificialLoad(false);
      },
    });

  useAppointmentThirdPartySubscription({
    variables: {
      appointmentId: appointmentCodexId,
    },
    onSubscriptionData: ({ subscriptionData }) => {
      if (subscriptionData.data?.appointments?.[0]?.is_prescription_ready) {
        setArtificialLoad(true);
        getFHIRMedicationsByPatientCodexId({
          variables: {
            patientCodexId,
            encounterId,
            source: [MedicationRequestSource.Fullscript],
          },
        });
      }
    },
  });

  const [openPrescriptionModal] = useModal(VirtualPharmaciesModal);

  const handlePrescribeMedication = async () => {
    if (!patientCodexId || !(appointmentCodexId || encounterId)) return;
    await openPrescriptionModal({
      patientId: patientCodexId,
      appointmentId: appointmentCodexId,
      encounterId: encounterId ?? undefined,
      onModalClose: () => {
        setArtificialLoad(true);
        getFHIRMedicationsByPatientCodexId({
          variables: {
            patientCodexId,
            encounterId,
          },
          onCompleted: () => {
            setTimeout(() => {
              setArtificialLoad(false);
            }, 5_000);
          },
        });
      },
    });
  };

  const medications = useMemo(
    () =>
      medicationsData?.getFHIRMedicationRequestByPatientCodexId
        ?.medication_requests ?? [],
    [medicationsData],
  );

  const loadMedication = networkLoading || artificialLoad;
  const lastMedication = medications.sort((a, b) =>
    moment(b.requestedAt).diff(a.requestedAt),
  )?.[0];

  if (
    !isEditMode &&
    !medicationsData?.getFHIRMedicationRequestByPatientCodexId
      .medication_requests?.length
  )
    return null;

  return (
    <Card
      title={title ?? locale?.title}
      titleClassName="flex text-h4 text-btn-black font-semibold"
      rightComponent={
        isEditMode &&
        medicationsData?.getFHIRMedicationRequestByPatientCodexId
          .medication_requests?.length ? (
          <ButtonComponent
            type="underline"
            testID="add-medication"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              handlePrescribeMedication();
            }}
            iconPosition="left"
          >
            Recommend supplements
          </ButtonComponent>
        ) : null
      }
      showSeparator
    >
      {loading || medicationLocaleLoading || loadMedication ? (
        <Spinner />
      ) : (
        <div className="w-full">
          {medicationsData?.getFHIRMedicationRequestByPatientCodexId
            .medication_requests?.length ? (
            <>
              <div className="flex flex-col border rounded-t-10 desktop:flex-row desktop:items-center items-start px-5 desktop:px-30  border-gray-opacity-15 py-4 bg-purple-50 overflow-hidden desktop:gap-30 gap:2.5">
                <h3 className="justify-start text-neutral-800 text-lg font-semibold leading-normal">
                  Recommended supplements
                </h3>
                <p className="justify-start text-med-gray-3 text-base font-medium leading-snug">
                  Prescribed date:{' '}
                  {moment(lastMedication.requestedAt).format(STANDARD_DATE)}
                </p>
              </div>
              <MedicationCard
                medication={
                  medicationsData?.getFHIRMedicationRequestByPatientCodexId
                    .medication_requests
                }
                className="rounded-t-none border-t-transparent"
                locale={medicationLocale}
                hideHeader
              />
            </>
          ) : (
            <MedicationPlaceholder
              onClickAddMedication={handlePrescribeMedication}
              label={locale.noMedications}
              supportText={locale.noMedicationsDescription}
              buttonText={locale.addMedication}
            />
          )}
        </div>
      )}
    </Card>
  );
};

export const MedicationsVeradigm = ({
  patientCodexId,
  appointmentCodexId,
  encounterId,
  title,
  providerCodexId,
  isEditMode = true,
}: MedicationsProps & { providerCodexId: string }) => {
  const navigate = useNavigate();
  const { addToast } = useCustomToast();
  const { data: locale, loading } = useGetComponent({
    locale: 'en',
    componentId: componentIds.MEDICATION,
  });

  const { data: medicationLocale, loading: medicationLocaleLoading } =
    useGetPage({
      locale: 'en',
      pageId: pageIds.MEDICATION_HISTORY,
    });

  useGetThirdPartyConfigQuery({
    context: {
      ignoreGlobalErrors: true,
    },
  });
  const [artificialLoad, setArtificialLoad] = useState(false);

  const [medicationsData, setMedicationsData] =
    useState<GetFhirMedicationRequestListQuery | null>();

  const [getFHIRMedicationsByPatientCodexId, { loading: networkLoading }] =
    useGetFhirMedicationRequestListLazyQuery({
      fetchPolicy: 'network-only',
      onCompleted: (data) => {
        setArtificialLoad(false);
        setMedicationsData(data);
      },
      onError: () => {
        setArtificialLoad(false);
      },
    });

  const onClose = useCallback(() => {
    navigate({ search: undefined }, { replace: true });
  }, [navigate]);

  useAppointmentThirdPartySubscription({
    variables: {
      appointmentId: appointmentCodexId,
    },
    onSubscriptionData: ({ subscriptionData }) => {
      if (subscriptionData.data?.appointments?.[0]?.is_prescription_ready) {
        setArtificialLoad(true);
        getFHIRMedicationsByPatientCodexId({
          variables: {
            patientCodexId,
            encounterId,
            source: [MedicationRequestSource.Veradigm],
          },
        });
      }
    },
  });

  const [getToken, { data, loading: loadVeradigm }] =
    usePrescribeVeradigmMutation({
      onError: (error) => {
        addToast({ type: 'warning', message: error.message });
      },
    });

  const [url] = useSearchParams();
  const isModalOpenVeradigm = url.get(VERADIGM);

  const handlePrescribeMedication = useCallback(
    () => navigate({ search: `?${VERADIGM}=true` }, { replace: true }),
    [navigate],
  );

  const [syncVeradigm] = useSyncEPrescribeMedicationsByPatientMutation({
    onCompleted: () => {
      addToast({
        type: 'success',
        message: 'Successfully synced medications from Veradigm',
      });
      onClose();
    },
    refetchQueries: [
      {
        query: GetFhirMedicationRequestListDocument,
        variables: {
          patientCodexId,
          encounterId,
          source: [MedicationRequestSource.Veradigm],
        },
      },
    ],
  });

  useEffect(() => {
    if (isModalOpenVeradigm === 'true' && patientCodexId) {
      getToken({
        variables: {
          mode: EPrescribeSsoMode.PatientLockDownMode,
          patientCodexId: patientCodexId ?? '',
        },
      });
    }
  }, [isModalOpenVeradigm, getToken, patientCodexId]);

  const loadMedication = networkLoading || artificialLoad;

  const medications = useMemo(
    () =>
      medicationsData?.getFHIRMedicationRequestByPatientCodexId
        ?.medication_requests ?? [],
    [medicationsData],
  );

  const lastMedication = medications.sort((a, b) =>
    moment(b.requestedAt).diff(a.requestedAt),
  )?.[0];

  if (
    !isEditMode &&
    !medicationsData?.getFHIRMedicationRequestByPatientCodexId
      .medication_requests?.length
  )
    return null;

  return (
    <>
      <Card
        title={title ?? locale?.title}
        titleClassName="flex text-h4 text-btn-black font-semibold"
        rightComponent={
          isEditMode &&
          medicationsData?.getFHIRMedicationRequestByPatientCodexId
            .medication_requests?.length ? (
            <ButtonComponent
              type="underline"
              testID="add-medication"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                handlePrescribeMedication();
              }}
              disabled={loadVeradigm}
              showSpinner
              iconPosition="left"
            >
              Recommend medication
            </ButtonComponent>
          ) : null
        }
        showSeparator
      >
        {loading || medicationLocaleLoading || loadMedication ? (
          <Spinner />
        ) : (
          <div className="w-full">
            {medicationsData?.getFHIRMedicationRequestByPatientCodexId
              .medication_requests?.length ? (
              <>
                <div className="flex flex-col border rounded-t-10 desktop:flex-row desktop:items-center items-start px-5 desktop:px-30  border-gray-opacity-15 py-4 bg-green-50 overflow-hidden desktop:gap-30 gap:2.5">
                  <h3 className="justify-start text-neutral-800 text-lg font-semibold leading-normal">
                    Medications prescribed
                  </h3>
                  <p className="justify-start text-med-gray-3 text-base font-medium leading-snug">
                    Prescribed date:{' '}
                    {moment(lastMedication.requestedAt).format(STANDARD_DATE)}
                  </p>
                </div>
                <MedicationCardVeradigm
                  className="rounded-t-none border-t-transparent"
                  medication={
                    medicationsData?.getFHIRMedicationRequestByPatientCodexId
                      .medication_requests
                  }
                  locale={medicationLocale}
                  hideHeader
                />
              </>
            ) : (
              <MedicationPlaceholder
                onClickAddMedication={handlePrescribeMedication}
                label={'No prescribed medications'}
                supportText={
                  'In this section, you can prescribe medications for the patient'
                }
                buttonText={'Prescribe medication'}
              />
            )}
          </div>
        )}
      </Card>
      {data?.ePrescribeJwt?.jwt && (
        <VeradigmModal
          token={data?.ePrescribeJwt?.jwt}
          isOpen={!!isModalOpenVeradigm}
          callback={() => {
            return new Promise(() =>
              syncVeradigm({
                variables: {
                  patientCodexId: patientCodexId as string,
                  providerCodexId: providerCodexId as string,
                  appointmentCodexId: appointmentCodexId as string,
                },
              }),
            );
          }}
          setIsOpen={onClose}
        />
      )}
    </>
  );
};

interface MedicationPlaceholderProps {
  onClickAddMedication?: () => void;
  label: string;
  supportText: string;
  buttonText: string;
}

function MedicationPlaceholder({
  onClickAddMedication,
  label,
  supportText,
  buttonText,
}: MedicationPlaceholderProps) {
  return (
    <div
      className={twMerge(
        'w-full h-[240px] p-4 flex flex-col gap-2.5 items-center justify-center rounded-lg',
      )}
    >
      <p className="font-semibold text-lg text-neutral-800">{label}</p>
      <p className="font-medium text-med-gray-3 text-center">{supportText}</p>

      <ButtonComponent
        Icon={PlusIcon}
        type="underline"
        testID="add-medication"
        onClick={onClickAddMedication}
        iconPosition="left"
      >
        {buttonText}
      </ButtonComponent>
    </div>
  );
}
